import React, { useEffect, useState } from 'react';
import DeviceExistsModal from './DeviceExistsModal';
import TwoFactorAuthModal from './TwoFactorAuthModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadBalancerAPI } from '../../api/load-balancer';
import { CircularProgress } from '@mui/material';
import { useRecoilState, useRecoilValue } from 'recoil';
import { authState, userStorageKey } from '../../api';
import TwoFAStateModal from './TwoFAStateModal';
import TwoFARecoveryModal from './TwoFARecoveryModal';

interface Modal2FAManagerProps {
  onComplete: () => void;
  deviceExists: boolean;
}

const ModalManager = ({ onComplete, deviceExists }: Modal2FAManagerProps) => {
  const [open2FAModalState, setOpen2FAModalState] = useState<boolean>(false);
  const [openDeviceExistsModal, setOpenDeviceExistsModal] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);
  const [codeEntered, setCodeEntered] = useState<boolean>(false);
  const [open2FAModal, setOpen2FAModal] = useState<boolean>(false);
  const [open2FARecoveryModal, setOpen2FARecoveryModal] = useState<boolean>(false);

  const open2FAModalHandler = () => {
    setOpen2FAModalState(true);
    setOpen2FARecoveryModal(false);
  };

  const handle2FAVerify = (code: string) => {
    setVerified(true);
    setCodeEntered(true);
    setOpen2FAModal(false);
    onComplete();
  };

  const handleDeviceExistsVerify = () => {
    setCodeEntered(true);
    setOpenDeviceExistsModal(false);
    onComplete();
  };

  const handleCreateNewDevice = () => {
    setOpen2FARecoveryModal(false);
    setOpen2FAModalState(false);
    setOpen2FAModal(true);
  };

  const handleOpenRecoveryModal = () => {
    setOpen2FARecoveryModal(true);
    setOpenDeviceExistsModal(false);
  };

  useEffect(() => {
    if (!codeEntered) {
      if (deviceExists) {
        setOpenDeviceExistsModal(true);
        setOpen2FAModalState(false);
      } else {
        open2FAModalHandler();
        setOpenDeviceExistsModal(false);
      }
    }
  }, [codeEntered, deviceExists]);

  return (
    <>
      {deviceExists ? (
        <>
          <DeviceExistsModal
            isOpen={openDeviceExistsModal}
            onClose={() => setOpenDeviceExistsModal(false)}
            onVerify={handleDeviceExistsVerify}
            onOpenRecoveryModal={handleOpenRecoveryModal}
            isMenuClick={false}
          />
          <TwoFARecoveryModal
            isOpen={open2FARecoveryModal}
            onClose={() => setOpen2FARecoveryModal(false)}
          ></TwoFARecoveryModal>
          <TwoFAStateModal
            isOpen={open2FAModalState}
            onClose={() => setOpen2FAModalState(false)}
            isClickMenu={false}
            onCreateNewDevice={handleCreateNewDevice}
          />
          <TwoFactorAuthModal isOpen={open2FAModal} onClose={() => setOpen2FAModal(false)} onVerify={handle2FAVerify} />
        </>
      ) : (
        <>
          <TwoFAStateModal
            isOpen={open2FAModalState}
            onClose={() => setOpen2FARecoveryModal(false)}
            isClickMenu={false}
            onCreateNewDevice={handleCreateNewDevice}
          />
          <TwoFactorAuthModal isOpen={open2FAModal} onClose={() => setOpen2FAModal(false)} onVerify={handle2FAVerify} />
        </>
      )}
    </>
  );
};

const TwoFactorAuthPage = () => {
  const [deviceExists, setDeviceExists] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { redirectTo } = location.state || { redirectTo: '/' };
  const [auth, setAuth] = useRecoilState(authState);
  const [refreshedToken, setRefreshedToken] = useState(false);
  const authStateValue = useRecoilValue(authState);

  const refreshToken = async () => {
    try {
      const auth = localStorage.getItem(userStorageKey);
      const parsedAuth = JSON.parse(auth || '{}');
      if (!parsedAuth.refreshToken) {
        throw new Error('No refresh token available');
      }

      const { data } = await loadBalancerAPI.apiInstance.refreshToken(parsedAuth.refreshToken);
      setAuth((prevState) => ({
        ...prevState,
        accessToken: data.data.accessToken,
        refreshToken: data.data.refreshToken,
        isLoading: false,
      }));
      setRefreshedToken(true);
    } catch (error) {
      console.error('Failed to refresh token:', error);
      navigate('/auth/login');
    }
  };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        if (!authStateValue?.user?.id) return;
        const res = await loadBalancerAPI.apiInstance.getTwoFAState(authStateValue?.user?.id);
        if (res?.data) {
          const { twoFAState } = res.data;
          if (twoFAState === null) {
            setDeviceExists(false);
          } else if (twoFAState === true) {
            const response = await loadBalancerAPI.apiInstance.getTwoFactorStatus();
            if (!response.data.data || !response.data.data.otpEnabled) {
              setDeviceExists(false);
            } else {
              setDeviceExists(true);
            }
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch status:', error);
        setIsLoading(false);
        navigate('/auth/login');
      }
    };
    fetchStatus();
    if (!location.state) {
      navigate('/home');
    }

    const interval = setInterval(() => {
      refreshToken();
      fetchStatus();
    }, 60000);
    return () => clearInterval(interval);
  }, [authStateValue?.user?.id, location.state, navigate, refreshedToken]);

  useEffect(() => {
    if (auth && !auth.accessToken && !auth.isLoading) {
      navigate('/auth/login');
    }
  }, [auth, navigate]);

  const handleModalsComplete = () => {
    navigate(redirectTo);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </div>
    );
  }

  return <ModalManager onComplete={handleModalsComplete} deviceExists={deviceExists} />;
};

export default TwoFactorAuthPage;
